import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { Dropdown } from '@elfsight/widget-modal';
import { getInvertedColor } from '@elfsight/widget-utils';
import type { CountryCode } from 'libphonenumber-js';
import { invertedColors } from '../colors';
import { COUNTRIES } from '../countries';
import { type Country } from '../types';
import { CountryList } from './CountryList';
import { SearchInput } from './SearchInput';

const COUNTRY_LIST_ID = 'es-phone-country-list';

interface CountryCodeDropdownProps {
  anchorElement: null | Element;
  open: boolean;
  onSelect: (country: Country) => void;
  width: number;
  onCloseRequest: (event?: PointerEvent) => void;
  fieldColor: string;
  fieldFillColor: string;
  fieldActiveColor: string;
  fieldBorderRadius: number;
  searchPlaceholderText: string;
  emptyCountryListMessage: string;
  ariaLabel: string;
  countries: Country[];
  selectedCountryCode: CountryCode;
  portalContainerElement?: Element;
}

export const CountryCodeDropdown = ({
  anchorElement,
  open,
  onSelect,
  width,
  onCloseRequest,
  fieldColor,
  fieldActiveColor,
  fieldFillColor,
  fieldBorderRadius,
  ariaLabel,
  emptyCountryListMessage,
  searchPlaceholderText,
  selectedCountryCode,
  countries = COUNTRIES,
  portalContainerElement
}: CountryCodeDropdownProps) => {
  const [searchValue, setSearchValue] = useState('');
  const itemContainerRef = useRef<HTMLDivElement | null>(null);
  const dropdownBorderRadius = Math.min(fieldBorderRadius, 30);
  const [itemContainerPaddingBottom, setItemContainerPaddingBottom] =
    useState(0);

  useLayoutEffect(() => {
    if (itemContainerRef.current) {
      const needsPadding =
        itemContainerRef.current.scrollHeight <=
        itemContainerRef.current.clientHeight;
      setItemContainerPaddingBottom(needsPadding ? 4 : 0);
    }
  }, [searchValue]);

  const filteredCountries = useMemo(() => {
    const queryLower = searchValue.trim().toLowerCase();

    const countryAlphaNumericSort = (a: Country, b: Country) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();

      const aStarts = aName.startsWith(queryLower);
      const bStarts = bName.startsWith(queryLower);
      if (aStarts !== bStarts) return aStarts ? -1 : 1;

      const aIncludes = aName.includes(queryLower);
      const bIncludes = bName.includes(queryLower);
      if (aIncludes !== bIncludes) return aIncludes ? -1 : 1;

      return aName.localeCompare(bName);
    };

    if (!queryLower) {
      return countries.sort(countryAlphaNumericSort);
    }

    const results = countries.filter(
      (country) =>
        country.name.toLowerCase().includes(queryLower) ||
        country.phoneCode.toLowerCase().includes(queryLower)
    );

    return results.sort(countryAlphaNumericSort);
  }, [searchValue, countries]);

  const portalContainer =
    portalContainerElement || anchorElement || document.body;

  return (
    <StyledDropdown
      _backgroundColor={getInvertedColor(fieldColor, invertedColors.background)}
      _dropdownBorderRadius={dropdownBorderRadius}
      _width={width}
      ariaLabel={ariaLabel}
      alignment="left"
      role="combobox"
      aria-expanded
      aria-controls={COUNTRY_LIST_ID}
      displayOver={false}
      portalContainerElement={portalContainer}
      withoutIcons
      closeOnClick={false}
      anchorElement={anchorElement}
      open={open}
      onCloseRequest={onCloseRequest}
      preventCloseOnAnchorElementClick
    >
      <SearchInput
        fieldFillColor={fieldFillColor}
        fieldColor={fieldColor}
        isOpen={open}
        dropdownBorderRadius={dropdownBorderRadius}
        fieldActiveColor={fieldActiveColor}
        searchPlaceholderText={searchPlaceholderText}
        value={searchValue}
        onChange={setSearchValue}
      />
      <ItemContainer
        ref={itemContainerRef}
        _scrollColor={fieldColor}
        _paddingBottom={itemContainerPaddingBottom}
        id={COUNTRY_LIST_ID}
        role="listbox"
      >
        <CountryList
          countries={filteredCountries}
          fieldColor={fieldColor}
          emptyCountryListMessage={emptyCountryListMessage}
          scrollContainer={itemContainerRef}
          selectedCountryCode={selectedCountryCode}
          onCloseRequest={onCloseRequest}
          onSelect={onSelect}
        />
      </ItemContainer>
    </StyledDropdown>
  );
};

const ItemContainer = styled.div<{
  _scrollColor: string;
  _paddingBottom: number;
}>`
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  scrollbar-width: auto;
  scrollbar-gutter: stable;
  padding-bottom: ${({ _paddingBottom }) => _paddingBottom}px;

  @supports (-moz-appearance: none) {
    scrollbar-width: thin;
    scrollbar-color: transparent;
  }

  &::-webkit-scrollbar {
    width: 6px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 6px;
    background-color: ${({ _scrollColor }) =>
      getColorWithAlpha(_scrollColor, 0.1)};

    &:hover {
      background-color: ${({ _scrollColor }) =>
        getColorWithAlpha(_scrollColor, 0.1)};
    }
  }
`;

const StyledDropdown = styled(Dropdown)<{
  _backgroundColor: string;
  _dropdownBorderRadius: number;
  _width: number;
}>`
  max-height: 344px;
  width: clamp(270px, ${({ _width }) => _width}px, 320px);
  display: flex;
  flex-direction: column;
  padding: 0;
  background-color: ${({ _backgroundColor }) => _backgroundColor};
  border-radius: ${({ _dropdownBorderRadius }) => _dropdownBorderRadius}px;
  overflow: hidden;
`;
